/**
 * Relationship/evidence integrity validation for the Defense Technology
 * Intelligence graph.
 *
 * Every generated evidence-bearing node (research, procurement, buyer,
 * supplier, agency, person, PLA org) must have at least one meaningful
 * evidence-supported edge — otherwise the graph would render unexplained
 * orphan nodes that cannot answer "why is this node on my screen?".
 *
 * Orphans indicate normalization or graph-adapter problems, so during
 * development they are logged as `[GraphIntegrity]` warnings.
 */
import {
  IntelNode,
  IntelNodeType,
  IntelligenceGraphSnapshot,
} from '../../types/intelligenceGraph';

/** Node types that must always trace back through at least one edge. */
const VALIDATED_TYPES: IntelNodeType[] = [
  'research',
  'procurement',
  'buyer',
  'supplier',
  'agency',
  'person',
  'pla',
];

export interface GraphIntegrityReport {
  /** Nodes of a validated type with zero edges. */
  orphans: IntelNode[];
  /** Orphans / validated-node ratio context. */
  validatedNodeCount: number;
}

/** Finds validated-type nodes that have no supporting edge at all. */
export function validateGraphIntegrity(
  snapshot: IntelligenceGraphSnapshot,
  rootId?: string | null,
): GraphIntegrityReport {
  const connected = new Set<string>();
  snapshot.edges.forEach((edge) => {
    connected.add(edge.source);
    connected.add(edge.target);
  });

  const validated = snapshot.nodes.filter(
    (node) =>
      node.id !== rootId && !node.isRoot && VALIDATED_TYPES.includes(node.type),
  );
  const orphans = validated.filter((node) => !connected.has(node.id));
  return { orphans, validatedNodeCount: validated.length };
}

/** Logs a development warning per orphan node found in the snapshot. */
export function logGraphIntegrityWarnings(report: GraphIntegrityReport): void {
  if (process.env.NODE_ENV === 'production') return;
  report.orphans.forEach((node) => {
    // eslint-disable-next-line no-console
    console.warn(
      `[GraphIntegrity] Orphan ${node.type} detected:`,
      `id=${node.id}`,
      `label=${node.label}`,
      `records=${node.recordCount}`,
    );
  });
}
